import ReceiptPrinterEncoder from "@point-of-sale/receipt-printer-encoder";
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClose,
  faSpinner,
  faCheckCircle,
  faMoneyBillWave,
  faCreditCard,
  faMobileAlt,
  faBank,
  faReceipt,
  faUtensils,
  faBagShopping,
  faMotorcycle,
  faPrint,
  faPlusCircle,
  faMinusCircle,
  faDeleteLeft,
  faPercent,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import MessageModal from "./MessageModal";

// Helper to fetch CSRF token
const fetchCsrfToken = async () => {
  const response = await fetch(
    `${process.env.REACT_APP_BASE_URL}/session/token`,
    {
      method: "GET",
      credentials: "include",
    },
  );
  if (!response.ok) {
    throw new Error("Failed to fetch CSRF token");
  }
  return await response.text();
};

// Numeric Keyboard Component - Touch Optimized
const NumericKeyboard = ({
  onKeyPress,
  onClear,
  onFullAmount,
  value,
  maxAmount,
}) => {
  const keys = [
    ["7", "8", "9"],
    ["4", "5", "6"],
    ["1", "2", "3"],
    ["00", "0", "."],
  ];

  const handleKeyClick = (key) => {
    let newValue = value || "";

    if (key === "00") {
      newValue = newValue + "00";
    } else {
      newValue = newValue + key;
    }

    if (key === "." && value && value.includes(".")) {
      return;
    }

    if (newValue.includes(".")) {
      const parts = newValue.split(".");
      if (parts[1] && parts[1].length > 2) {
        return;
      }
    }

    onKeyPress(newValue);
  };

  return (
    <div style={numericKeyboardStyles.container}>
      <div style={numericKeyboardStyles.keypad}>
        {keys.map((row, rowIndex) => (
          <div key={rowIndex} style={numericKeyboardStyles.row}>
            {row.map((key) => (
              <button
                key={key}
                style={numericKeyboardStyles.key}
                onClick={() => handleKeyClick(key)}
                type="button"
              >
                {key}
              </button>
            ))}
          </div>
        ))}
        <div style={numericKeyboardStyles.doubleRow}>
          <button
            style={{
              ...numericKeyboardStyles.key,
              ...numericKeyboardStyles.clearKey,
              flex: 1,
            }}
            onClick={onClear}
            type="button"
          >
            <FontAwesomeIcon icon={faDeleteLeft} /> Clear
          </button>
          <button
            style={{
              ...numericKeyboardStyles.key,
              ...numericKeyboardStyles.fullAmountKey,
              flex: 1,
            }}
            onClick={onFullAmount}
            type="button"
          >
            Full Amount
          </button>
        </div>
      </div>
    </div>
  );
};

const numericKeyboardStyles = {
  container: {
    padding: "12px",
    backgroundColor: "#F1F5F9",
    borderRadius: "12px",
    border: "1px solid #E2E8F0",
  },
  keypad: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "8px",
  },
  doubleRow: {
    display: "flex",
    gap: "8px",
  },
  key: {
    padding: "14px",
    backgroundColor: "white",
    border: "2px solid #CBD5E1",
    borderRadius: "10px",
    fontSize: "22px",
    fontWeight: "700",
    cursor: "pointer",
    transition: "all 0.1s",
    textAlign: "center",
    minHeight: "56px",
    minWidth: "48px",
    height: "56px",
  },
  clearKey: {
    backgroundColor: "#EF4444",
    color: "white",
    border: "none",
    fontSize: "18px",
  },
  fullAmountKey: {
    backgroundColor: "#10B981",
    color: "white",
    border: "none",
    fontSize: "18px",
  },
};

export default function PayBillsModal({ order, onClose, onSuccess }) {
  const orderId = order?.id;
  const orderStatus = order?.status;
  const isDineIn = order?.order_type === "dine-in";

  const initialOrderTotal =
    order?.original_total || order?.menu_subtotal || order?.total || 0;

  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [changeAmount, setChangeAmount] = useState(0);
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [billData, setBillData] = useState(null);
  const [fetchingBill, setFetchingBill] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [removingServiceCharge, setRemovingServiceCharge] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const [hasServiceCharge, setHasServiceCharge] = useState(false);
  const [serviceChargePercentage, setServiceChargePercentage] = useState(5);
  const [serviceChargeAmount, setServiceChargeAmount] = useState(0);
  const [menuSubtotal, setMenuSubtotal] = useState(initialOrderTotal);
  const [grandTotal, setGrandTotal] = useState(initialOrderTotal);
  const [updatingServiceCharge, setUpdatingServiceCharge] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [isDiscountApplied, setIsDiscountApplied] = useState(false);

  const discountOptions = [5, 10, 15, 20];

  const [printerIP, setPrinterIP] = useState("192.168.1.233");
  const [printerPort, setPrinterPort] = useState(9100);

  const [vatAmount, setVatAmount] = useState(0);
  const [nhilAmount, setNhilAmount] = useState(0);
  const [getfundAmount, setGetfundAmount] = useState(0);
  const [tourismAmount, setTourismAmount] = useState(0);

  const [showOrderSummaryModal, setShowOrderSummaryModal] = useState(false);

  // Function to open cash drawer without printing receipt
  const openCashDrawer = async () => {
    if (printing) return;
    setPrinting(true);
    setErrorMessage("");

    try {
      localStorage.setItem("printer_ip", printerIP);
      localStorage.setItem("printer_port", printerPort.toString());

      const encoder = new ReceiptPrinterEncoder({
        language: "esc-pos",
        type: "epson",
      });

      // Just send the pulse command to open drawer
      encoder.initialize().pulse();

      const data = encoder.encode();
      const dataArray = Array.from(data);

      const response = await fetch("http://localhost:3001/print", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ip: printerIP,
          port: printerPort,
          data: dataArray,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to open cash drawer");
      }

      console.log("✅ Cash drawer opened successfully!");
    } catch (error) {
      console.error("Open drawer failed:", error);
      setErrorMessage(`Failed to open drawer: ${error.message}`);
      setModalError(true);
    } finally {
      setPrinting(false);
    }
  };

  // Force close function that ensures the modal is closed
  const forceClose = () => {
    if (isClosing) return;
    setIsClosing(true);

    // Close all child modals
    setModalSuccessMsg(false);
    setModalError(false);
    setShowOrderSummaryModal(false);

    // Close the main modal
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (orderId) {
      fetchBillData();
    }
    const savedIP = localStorage.getItem("printer_ip");
    const savedPort = localStorage.getItem("printer_port");
    if (savedIP) setPrinterIP(savedIP);
    if (savedPort) setPrinterPort(parseInt(savedPort));
  }, [orderId]);

  useEffect(() => {
    if (
      !orderId ||
      orderStatus === "paid" ||
      orderStatus === "refunded" ||
      orderStatus === "partially_refunded"
    ) {
      forceClose();
    }
  }, [orderId, orderStatus]);

  if (
    !orderId ||
    orderStatus === "paid" ||
    orderStatus === "refunded" ||
    orderStatus === "partially_refunded"
  ) {
    return null;
  }

  const calculateGrandTotal = (subtotal, includeServiceCharge = true) => {
    const scPercentage = isDineIn && includeServiceCharge ? 5 : 0;
    const serviceCharge = subtotal * (scPercentage / 100);
    return subtotal + serviceCharge;
  };

  const calculateTaxBreakdown = (subtotal) => {
    const taxRate = 0.21;
    const taxExclusiveAmount = subtotal / (1 + taxRate);
    return {
      taxExclusiveAmount: taxExclusiveAmount,
      vat: taxExclusiveAmount * 0.15,
      nhil: taxExclusiveAmount * 0.025,
      getfund: taxExclusiveAmount * 0.025,
      tourism: taxExclusiveAmount * 0.01,
    };
  };

  const handleDiscountSelect = (percent) => {
    if (selectedDiscount === percent && isDiscountApplied) {
      setSelectedDiscount(null);
      setIsDiscountApplied(false);
      setDiscountPercent(0);
      setDiscountAmount(0);

      const newGrandTotal = calculateGrandTotal(menuSubtotal, true);
      setGrandTotal(newGrandTotal);

      const scPercentage = isDineIn ? 5 : 0;
      const serviceCharge = menuSubtotal * (scPercentage / 100);
      setServiceChargeAmount(serviceCharge);

      const taxBreakdown = calculateTaxBreakdown(menuSubtotal);
      setVatAmount(taxBreakdown.vat);
      setNhilAmount(taxBreakdown.nhil);
      setGetfundAmount(taxBreakdown.getfund);
      setTourismAmount(taxBreakdown.tourism);

      setSuccessMessage("✅ Discount removed!");
      setModalSuccessMsg(true);
      setTimeout(() => setModalSuccessMsg(false), 3000);
      return;
    }

    setSelectedDiscount(percent);
    setIsDiscountApplied(true);

    const amount = (menuSubtotal * percent) / 100;
    setDiscountPercent(percent);
    setDiscountAmount(amount);

    const discountedSubtotal = menuSubtotal - amount;
    const newGrandTotal = calculateGrandTotal(discountedSubtotal, true);
    setGrandTotal(newGrandTotal);

    const scPercentage = isDineIn ? 5 : 0;
    const serviceCharge = discountedSubtotal * (scPercentage / 100);
    setServiceChargeAmount(serviceCharge);

    const taxBreakdown = calculateTaxBreakdown(discountedSubtotal);
    setVatAmount(taxBreakdown.vat);
    setNhilAmount(taxBreakdown.nhil);
    setGetfundAmount(taxBreakdown.getfund);
    setTourismAmount(taxBreakdown.tourism);

    setSuccessMessage(`✅ ${percent}% discount applied!`);
    setModalSuccessMsg(true);
    setTimeout(() => setModalSuccessMsg(false), 3000);
  };

  const printBill = async (shouldOpenDrawer = false) => {
    if (printing) return;
    setPrinting(true);
    setErrorMessage("");

    try {
      localStorage.setItem("printer_ip", printerIP);
      localStorage.setItem("printer_port", printerPort.toString());

      const formatCurrency = (amount) =>
        `GHC${parseFloat(amount || 0).toFixed(2)}`;

      const LINE_WIDTH = 42;
      const COL_WIDTHS = {
        item: 22,
        qty: 5,
        price: 10,
        label: 28,
        value: 14,
      };

      const padRight = (str, len) => {
        const s = String(str || "");
        return s.length > len ? s.substring(0, len) : s.padEnd(len);
      };

      const padLeft = (str, len) => {
        const s = String(str || "");
        return s.length > len ? s.substring(0, len) : s.padStart(len);
      };

      const formatItemLine = (name, qty, price) => {
        return `${padRight(name, COL_WIDTHS.item)}${padLeft(String(qty), COL_WIDTHS.qty)}${padLeft(price, COL_WIDTHS.price)}`;
      };

      const formatTotalLine = (label, amount) => {
        return `${padRight(label, COL_WIDTHS.label)}${padLeft(amount, COL_WIDTHS.value)}`;
      };

      const line = "=".repeat(LINE_WIDTH);
      const dashLine = "-".repeat(LINE_WIDTH);
      const orderIdStr = String(order?.id || "N/A").padStart(6, "0");

      // ✅ Use order items directly
      const allItems = order?.items || [];
      const receiptGuestName = order?.guest_name || "Guest";
      const receiptTableNumber = order?.table_number || "";
      const receiptWaiter = order?.waiter_name || "";

      const orderTypeLabel =
        order?.order_type === "dine-in"
          ? "DINE-IN"
          : order?.order_type === "takeaway"
            ? "TAKEAWAY"
            : "DELIVERY";

      const now = new Date();
      const dateStr = now.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      const timeStr = now.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });

      const paymentMethodLabel = paymentMethod.toUpperCase().replace("_", " ");
      const amountPaid = parseFloat(paymentAmount) || grandTotal;
      const changeDue = changeAmount;

      // ✅ Calculate totals from active items (exclude voided)
      const subtotal = allItems.reduce((sum, item) => {
        if (item.status === "voided") return sum;
        const remainingQty = item.quantity - (item.refunded_quantity || 0);
        return sum + item.price * remainingQty;
      }, 0);

      const discount = order?.discount || 0;
      const hasDiscount = discount > 0;
      const discountedSubtotal = subtotal - discount;

      const isDineIn = order?.order_type === "dine-in";
      const serviceChargePercentage =
        order?.service_charge_percentage || (isDineIn ? 5 : 0);
      const serviceChargeAmount =
        discountedSubtotal * (serviceChargePercentage / 100);
      const hasServiceCharge = isDineIn && serviceChargeAmount > 0;
      const grandTotalCalc = discountedSubtotal + serviceChargeAmount;

      // Calculate tax breakdown
      const taxRate = 0.21;
      const taxExclusiveAmount = discountedSubtotal / (1 + taxRate);
      const vatAmount = taxExclusiveAmount * 0.15;
      const nhilAmount = taxExclusiveAmount * 0.025;
      const getfundAmount = taxExclusiveAmount * 0.025;
      const tourismAmount = taxExclusiveAmount * 0.01;

      const encoder = new ReceiptPrinterEncoder({
        language: "esc-pos",
        type: "epson",
      });

      encoder
        .initialize()
        .align("center")
        .size(2)
        .bold(true)
        .line("THE HEADQUARTERS EATERY")
        .size(1)
        .bold(false)
        .line("21st Otele Avenue, Accra")
        .line("TIN: P0013030590")
        .line(line)
        .bold(true)
        .line(`ORDER #${orderIdStr}`)
        .bold(false)
        .line(`${dateStr}   ${timeStr}`)
        .line(`${orderTypeLabel}`)
        .line(line)
        .newline()
        .align("left")
        .line(`Customer: ${receiptGuestName}`)
        .line(`Table:    ${receiptTableNumber || "N/A"}`)
        .line(`Waiter:   ${receiptWaiter || "N/A"}`)
        .newline()
        .align("center")
        .line(dashLine)
        .align("left")
        .line(formatItemLine("Item Description", "Qty", "Price"))
        .line(dashLine);

      // ✅ Only print active (non-voided) items
      let hasRefundedItems = false;
      allItems.forEach((item) => {
        if (item.status === "voided") return;

        const qty = item.quantity || 0;
        const price = item.price || 0;
        const total = qty * price;
        const name = (item.name || "Unknown").substring(0, COL_WIDTHS.item);
        const refundedQty = item.refunded_quantity || 0;
        const remainingQty = qty - refundedQty;

        if (remainingQty > 0) {
          encoder.line(formatItemLine(name, qty.toString(), total.toFixed(2)));
          if (refundedQty > 0) {
            hasRefundedItems = true;
            encoder.line(`  (Refunded: -${refundedQty})`);
          }
        }
      });

      if (hasRefundedItems) {
        encoder.newline();
      }

      encoder
        .newline()
        .align("center")
        .line(dashLine)
        .align("left")
        .line(formatTotalLine("Subtotal", formatCurrency(subtotal)));

      if (hasDiscount) {
        encoder.line(
          formatTotalLine(
            `Discount (${discount}%)`,
            `-${formatCurrency(discount)}`,
          ),
        );
      }

      if (hasServiceCharge) {
        encoder.line(
          formatTotalLine(
            `Service (${serviceChargePercentage}%)`,
            formatCurrency(serviceChargeAmount),
          ),
        );
      }

      if (
        vatAmount > 0 ||
        nhilAmount > 0 ||
        getfundAmount > 0 ||
        tourismAmount > 0
      ) {
        encoder.line(dashLine);
        encoder.line(formatTotalLine("Tax Breakdown", ""));
        if (vatAmount > 0) {
          encoder.line(formatTotalLine("VAT 15%", formatCurrency(vatAmount)));
        }
        if (nhilAmount > 0) {
          encoder.line(
            formatTotalLine("NHIL 2.5%", formatCurrency(nhilAmount)),
          );
        }
        if (getfundAmount > 0) {
          encoder.line(
            formatTotalLine("GETFund 2.5%", formatCurrency(getfundAmount)),
          );
        }
        if (tourismAmount > 0) {
          encoder.line(
            formatTotalLine("Tourism 1%", formatCurrency(tourismAmount)),
          );
        }
      }

      encoder
        .align("center")
        .line(line)
        .bold(true)
        .line(formatTotalLine("TOTAL", formatCurrency(grandTotalCalc)))
        .bold(false)
        .line(line)
        .newline()
        .align("left")
        .line(`Payment: ${paymentMethodLabel}`)
        .line(`Amount:  ${formatCurrency(amountPaid)}`);

      if (changeDue > 0) {
        encoder.line(`Change:  ${formatCurrency(changeDue)}`);
      }

      encoder
        .newline()
        .align("center")
        .line(dashLine)
        .line("Thank you for dining with us!")
        .line("Have a great day!")
        .line(line)
        .newline()
        .line("Software by: Reactive System Developers")
        .line("0257335982")
        .newline()
        .newline()
        .cut("partial");

      if (shouldOpenDrawer) {
        encoder.pulse();
      }

      const data = encoder.encode();
      const dataArray = Array.from(data);

      const response = await fetch("http://localhost:3001/print", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ip: printerIP,
          port: printerPort,
          data: dataArray,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to print bill");
      }

      setModalSuccessMsg(true);
      setSuccessMessage(
        shouldOpenDrawer
          ? "✅ Receipt printed & cash drawer opened!"
          : "✅ Receipt printed successfully!",
      );
      setTimeout(() => {
        setModalSuccessMsg(false);
      }, 3000);
    } catch (error) {
      console.error("Print failed:", error);
      setErrorMessage(`Print failed: ${error.message}`);
      setModalError(true);
    } finally {
      setPrinting(false);
    }
  };

  const fetchBillData = async () => {
    setFetchingBill(true);
    try {
      const csrfToken = await fetchCsrfToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/order/${order.id}/bill`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
        },
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.bill) {
          setBillData(data.bill);

          const subtotal =
            data.bill.menu_subtotal ||
            order?.original_total ||
            order?.menu_subtotal ||
            order?.total ||
            0;
          setMenuSubtotal(subtotal);

          const taxBreakdown = calculateTaxBreakdown(subtotal);
          setVatAmount(taxBreakdown.vat);
          setNhilAmount(taxBreakdown.nhil);
          setGetfundAmount(taxBreakdown.getfund);
          setTourismAmount(taxBreakdown.tourism);

          setSelectedDiscount(null);
          setDiscountPercent(0);
          setDiscountAmount(0);
          setIsDiscountApplied(false);

          if (isDineIn) {
            const percentage =
              data.bill.service_charge_percentage !== undefined
                ? data.bill.service_charge_percentage
                : 5;

            if (data.bill.grand_total) {
              setGrandTotal(data.bill.grand_total);
              setHasServiceCharge(data.bill.service_charge_applied || false);
              setServiceChargeAmount(data.bill.service_charge || 0);
              setServiceChargePercentage(percentage);
            } else {
              const serviceCharge = subtotal * (percentage / 100);
              setGrandTotal(subtotal + serviceCharge);
              setHasServiceCharge(percentage > 0);
              setServiceChargeAmount(serviceCharge);
              setServiceChargePercentage(percentage);
            }
          } else {
            setGrandTotal(subtotal);
            setHasServiceCharge(false);
            setServiceChargeAmount(0);
            setServiceChargePercentage(0);
          }
        }
      } else {
        calculateChargesFromOrder();
      }
    } catch (error) {
      console.error("Error fetching bill data:", error);
      calculateChargesFromOrder();
    } finally {
      setFetchingBill(false);
    }
  };

  const calculateChargesFromOrder = () => {
    const subtotal =
      order?.original_total || order?.menu_subtotal || order?.total || 0;
    setMenuSubtotal(subtotal);

    const taxBreakdown = calculateTaxBreakdown(subtotal);
    setVatAmount(taxBreakdown.vat);
    setNhilAmount(taxBreakdown.nhil);
    setGetfundAmount(taxBreakdown.getfund);
    setTourismAmount(taxBreakdown.tourism);

    if (isDineIn) {
      const servicePercent = 5;
      const serviceCharge = subtotal * (servicePercent / 100);

      setServiceChargeAmount(serviceCharge);
      setServiceChargePercentage(servicePercent);
      setHasServiceCharge(true);
      setGrandTotal(subtotal + serviceCharge);
    } else {
      setGrandTotal(subtotal);
      setServiceChargeAmount(0);
      setHasServiceCharge(false);
    }
  };

  const addServiceCharge = async () => {
    if (!isDineIn) {
      setErrorMessage("Service charge is only available for dine-in orders");
      setModalError(true);
      return;
    }

    if (hasServiceCharge) return;

    setUpdatingServiceCharge(true);
    try {
      const csrfToken = await fetchCsrfToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/order/${order.id}/service-charge`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify({ service_charge_percentage: 5 }),
        },
      );

      if (response.ok) {
        const data = await response.json();
        setServiceChargeAmount(data.service_charge);
        setServiceChargePercentage(data.service_charge_percentage);
        setGrandTotal(data.grand_total);
        setHasServiceCharge(true);
        await fetchBillData();
      } else {
        const discountedSubtotal = menuSubtotal - discountAmount;
        const serviceCharge = discountedSubtotal * 0.05;
        setServiceChargeAmount(serviceCharge);
        setGrandTotal(discountedSubtotal + serviceCharge);
        setHasServiceCharge(true);
      }
    } catch (error) {
      console.error("Error adding service charge:", error);
      const discountedSubtotal = menuSubtotal - discountAmount;
      const serviceCharge = discountedSubtotal * 0.05;
      setServiceChargeAmount(serviceCharge);
      setGrandTotal(discountedSubtotal + serviceCharge);
      setHasServiceCharge(true);
    } finally {
      setUpdatingServiceCharge(false);
    }
  };

  const handleRemoveServiceCharge = async () => {
    if (!isDineIn || !hasServiceCharge) return;

    setRemovingServiceCharge(true);
    try {
      const csrfToken = await fetchCsrfToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/order/${order.id}/service-charge/remove`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify({ reason: "Customer requested waiver" }),
        },
      );

      if (response.ok) {
        const data = await response.json();
        const discountedSubtotal = menuSubtotal - discountAmount;
        setServiceChargeAmount(0);
        setGrandTotal(discountedSubtotal);
        setHasServiceCharge(false);
        await fetchBillData();
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error || "Failed to remove service charge");
        setModalError(true);
      }
    } catch (error) {
      console.error("Error removing service charge:", error);
      setErrorMessage("Network error. Please try again.");
      setModalError(true);
    } finally {
      setRemovingServiceCharge(false);
    }
  };

  const handleAmountChange = (value) => {
    if (value.startsWith("0") && !value.startsWith("0.") && value.length > 1) {
      value = value.replace(/^0+/, "");
    }
    setPaymentAmount(value);
    const amount = parseFloat(value) || 0;
    if (amount >= grandTotal) {
      setChangeAmount(amount - grandTotal);
    } else {
      setChangeAmount(0);
    }
  };

  const handleClearAmount = () => {
    setPaymentAmount("");
    setChangeAmount(0);
  };

  const handleFullAmount = () => {
    setPaymentAmount(grandTotal.toString());
    setChangeAmount(0);
  };

  const getOrderTypeInfo = () => {
    switch (order.order_type) {
      case "dine-in":
        return { icon: faUtensils, label: "Dine In", color: "#FF6B35" };
      case "takeaway":
        return { icon: faBagShopping, label: "Takeaway", color: "#10B981" };
      case "delivery":
        return { icon: faMotorcycle, label: "Delivery", color: "#3B82F6" };
      default:
        return { icon: faUtensils, label: "Dine In", color: "#FF6B35" };
    }
  };

  const orderTypeInfo = getOrderTypeInfo();

  const handlePayment = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isClosing) return;

    if (
      order.status === "paid" ||
      order.status === "refunded" ||
      order.status === "partially_refunded"
    ) {
      setErrorMessage(
        `This order has already been ${order.status}. Payment cannot be processed.`,
      );
      setModalError(true);
      return;
    }

    if (paymentCompleted) return;

    if (paymentMethod === "cash") {
      const amount = parseFloat(paymentAmount);
      if (isNaN(amount) || amount < grandTotal) {
        setErrorMessage(`Amount must be at least GHS ${grandTotal.toFixed(2)}`);
        setModalError(true);
        return;
      }
    }

    setLoading(true);

    let csrfToken;
    try {
      csrfToken = await fetchCsrfToken();
    } catch (error) {
      console.error("Failed to fetch CSRF token:", error);
      setErrorMessage("Authentication error. Please refresh the page.");
      setModalError(true);
      setLoading(false);
      return;
    }

    const payload = {
      payment_method: paymentMethod,
      amount_received: parseFloat(paymentAmount) || grandTotal,
      amount_due: grandTotal,
      service_charge_applied: isDineIn && hasServiceCharge,
      service_charge_percentage:
        isDineIn && hasServiceCharge ? serviceChargePercentage : 0,
      service_charge_amount: isDineIn ? serviceChargeAmount : 0,
      discount_applied: isDiscountApplied,
      discount_percentage: discountPercent,
      discount_amount: discountAmount,
      notes: JSON.stringify({
        service_charge: {
          applied: isDineIn && hasServiceCharge,
          percentage:
            isDineIn && hasServiceCharge ? serviceChargePercentage : 0,
          amount: isDineIn ? serviceChargeAmount : 0,
        },
        discount: {
          applied: isDiscountApplied,
          percentage: discountPercent,
          amount: discountAmount,
        },
      }),
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/order/${order.id}/process-payment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );

      if (response.ok) {
        const responseData = await response.json();
        setPaymentCompleted(true);

        // ✅ Open cash drawer WITHOUT printing receipt
        await openCashDrawer();

        setLoading(false);

        // Call onSuccess callback if provided
        if (onSuccess) onSuccess();

        // Force close the PayBillsModal
        forceClose();
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error || "Payment failed. Please try again.");
        setModalError(true);
        setLoading(false);
      }
    } catch (err) {
      console.error("Payment error:", err);
      setErrorMessage("Network error. Please check your connection.");
      setModalError(true);
      setLoading(false);
    }
  };

  const paymentMethods = [
    { value: "cash", label: "Cash", icon: faMoneyBillWave, color: "#4CAF50" },
    { value: "card", label: "Card", icon: faCreditCard, color: "#2196F3" },
    {
      value: "mobile_money",
      label: "Mobile Money",
      icon: faMobileAlt,
      color: "#9C27B0",
    },
    {
      value: "bank_transfer",
      label: "Bank Transfer",
      icon: faBank,
      color: "#FF9800",
    },
  ];

  if (fetchingBill) {
    return (
      <div className="modal" style={touchStyles.modalOverlay}>
        <div className="overlay"></div>
        <div className="modal-content" style={touchStyles.modalContent}>
          <div
            className="modal-body"
            style={{ textAlign: "center", padding: "60px" }}
          >
            <FontAwesomeIcon icon={faSpinner} spin size="4x" color="#FF6B35" />
            <p style={{ marginTop: "20px", fontSize: "18px" }}>
              Loading bill details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="modal" style={touchStyles.modalOverlay}>
        <div className="overlay" onClick={forceClose}></div>
        <div className="modal-content" style={touchStyles.modalContent}>
          <div style={touchStyles.modalHeader}>
            <p style={touchStyles.modalTitle}>Pay Bills - Order #{order.id}</p>
            <button style={touchStyles.closeButton} onClick={forceClose}>
              <FontAwesomeIcon icon={faClose} size="lg" />
            </button>
          </div>

          <div style={touchStyles.twoColumnLayout}>
            <div style={touchStyles.leftColumn}>
              <div style={touchStyles.paymentMethodsSection}>
                <label style={touchStyles.label}>Payment Method *</label>
                <div style={touchStyles.paymentMethodsGrid}>
                  {paymentMethods.map((method) => (
                    <button
                      key={method.value}
                      type="button"
                      style={{
                        ...touchStyles.paymentMethodButton,
                        ...(paymentMethod === method.value
                          ? {
                              borderColor: method.color,
                              backgroundColor: `${method.color}20`,
                              transform: "scale(1.02)",
                            }
                          : {}),
                      }}
                      onClick={() => {
                        setPaymentMethod(method.value);
                        if (method.value !== "cash") {
                          setPaymentAmount("");
                          setChangeAmount(0);
                        }
                      }}
                    >
                      <FontAwesomeIcon
                        icon={method.icon}
                        style={{ color: method.color, fontSize: "20px" }}
                      />
                      <span style={touchStyles.paymentMethodLabel}>
                        {method.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* DISCOUNT SECTION */}
              <div style={touchStyles.discountSection}>
                <div style={touchStyles.discountHeader}>
                  <FontAwesomeIcon
                    icon={faPercent}
                    style={{ color: "#FF6B35", fontSize: "16px" }}
                  />
                  <span style={touchStyles.discountTitle}>Apply Discount</span>
                  {isDiscountApplied && (
                    <span style={touchStyles.discountStatus}>
                      {discountPercent}% Applied
                    </span>
                  )}
                </div>

                <div style={touchStyles.discountButtons}>
                  {discountOptions.map((percent) => (
                    <button
                      key={percent}
                      style={{
                        ...touchStyles.discountButton,
                        backgroundColor:
                          selectedDiscount === percent && isDiscountApplied
                            ? "#FF6B35"
                            : "#F3F4F6",
                        color:
                          selectedDiscount === percent && isDiscountApplied
                            ? "white"
                            : "#1F2937",
                        border:
                          selectedDiscount === percent && isDiscountApplied
                            ? "2px solid #FF6B35"
                            : "2px solid transparent",
                        transform:
                          selectedDiscount === percent && isDiscountApplied
                            ? "scale(1.05)"
                            : "scale(1)",
                      }}
                      onClick={() => handleDiscountSelect(percent)}
                    >
                      {percent}%
                      {selectedDiscount === percent && isDiscountApplied && (
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          style={{ marginLeft: "6px", fontSize: "12px" }}
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Service Charge and Details Button */}
              {isDineIn && (
                <div style={touchStyles.serviceChargeSection}>
                  <div style={touchStyles.serviceChargeRow}>
                    <div style={touchStyles.serviceChargeButtons}>
                      {!hasServiceCharge ? (
                        <button
                          type="button"
                          style={touchStyles.addServiceChargeButton}
                          onClick={addServiceCharge}
                          disabled={updatingServiceCharge}
                        >
                          <FontAwesomeIcon
                            icon={faPlusCircle}
                            style={{ marginRight: "8px" }}
                          />{" "}
                          Add Service (5%)
                        </button>
                      ) : (
                        <button
                          type="button"
                          style={touchStyles.removeServiceChargeButton}
                          onClick={handleRemoveServiceCharge}
                          disabled={
                            updatingServiceCharge || removingServiceCharge
                          }
                        >
                          {removingServiceCharge ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            <>
                              <FontAwesomeIcon
                                icon={faMinusCircle}
                                style={{ marginRight: "8px" }}
                              />{" "}
                              Remove +GHS {serviceChargeAmount.toFixed(2)}
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    <button
                      type="button"
                      style={touchStyles.detailsButton}
                      onClick={() => setShowOrderSummaryModal(true)}
                    >
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        style={{ marginRight: "8px" }}
                      />
                      Details
                    </button>
                  </div>
                </div>
              )}

              <div style={touchStyles.totalSection}>
                <div style={touchStyles.totalRow}>
                  <span style={touchStyles.totalLabel}>Total Amount:</span>
                  <span style={touchStyles.totalAmount}>
                    GHS {grandTotal.toFixed(2)}
                  </span>
                </div>
              </div>

              <div style={touchStyles.actionButtons}>
                <button
                  type="button"
                  style={touchStyles.printBillButton}
                  onClick={() => printBill(false)}
                  disabled={printing}
                >
                  {printing ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <>
                      <FontAwesomeIcon
                        icon={faPrint}
                        style={{ marginRight: "10px" }}
                      />
                      Print Bill
                    </>
                  )}
                </button>

                <button
                  type="button"
                  style={touchStyles.completePayButton}
                  onClick={handlePayment}
                  disabled={
                    loading ||
                    isClosing ||
                    (paymentMethod === "cash" &&
                      (!paymentAmount ||
                        parseFloat(paymentAmount) < grandTotal))
                  }
                >
                  {loading ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    "Complete Payment"
                  )}
                </button>
              </div>
            </div>

            <div style={touchStyles.rightColumn}>
              {paymentMethod === "cash" && (
                <div style={touchStyles.cashSection}>
                  <label style={touchStyles.label}>Payment Summary</label>

                  <div style={touchStyles.paymentSummaryRow}>
                    <div style={touchStyles.amountCard}>
                      <div style={touchStyles.amountCardLabel}>
                        Amount Paid (GHS)
                      </div>
                      <div style={touchStyles.amountCardValue}>
                        GHS {paymentAmount || "0.00"}
                      </div>
                    </div>

                    <div
                      style={{
                        ...touchStyles.amountCard,
                        backgroundColor:
                          changeAmount > 0 ? "#E8F5E9" : "#F8FAFC",
                        border:
                          changeAmount > 0
                            ? "2px solid #4CAF50"
                            : "2px solid #E2E8F0",
                      }}
                    >
                      <div style={touchStyles.amountCardLabel}>Change Due</div>

                      <div
                        style={{
                          ...touchStyles.amountCardValue,
                          color: changeAmount > 0 ? "#2E7D32" : "#64748B",
                        }}
                      >
                        GHS {changeAmount.toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <NumericKeyboard
                    onKeyPress={handleAmountChange}
                    onClear={handleClearAmount}
                    onFullAmount={handleFullAmount}
                    value={paymentAmount}
                    maxAmount={grandTotal}
                  />
                </div>
              )}
              {paymentMethod !== "cash" && (
                <div style={touchStyles.nonCashMessage}>
                  <FontAwesomeIcon
                    icon={faCreditCard}
                    style={{ marginRight: "8px" }}
                  />
                  Click "Complete Payment" to process payment via{" "}
                  {paymentMethod.replace("_", " ")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ORDER SUMMARY MODAL */}
      {showOrderSummaryModal && (
        <div className="modal" style={touchStyles.modalOverlay}>
          <div className="overlay"></div>
          <div
            className="modal-content"
            style={{ ...touchStyles.modalContent, maxWidth: "500px" }}
          >
            <div style={touchStyles.modalHeader}>
              <p style={touchStyles.modalTitle}>Order Summary</p>
              <button
                style={touchStyles.closeButton}
                onClick={() => setShowOrderSummaryModal(false)}
              >
                <FontAwesomeIcon icon={faClose} size="lg" />
              </button>
            </div>

            <div style={{ padding: "20px" }}>
              <div style={touchStyles.summaryRow}>
                <span style={touchStyles.summaryLabel}>Order Type:</span>
                <span style={touchStyles.summaryValue}>
                  {orderTypeInfo.label}
                </span>
              </div>
              {order.table_number && (
                <div style={touchStyles.summaryRow}>
                  <span style={touchStyles.summaryLabel}>Table:</span>
                  <span style={touchStyles.summaryValue}>
                    {order.table_number}
                  </span>
                </div>
              )}
              <div style={touchStyles.summaryRow}>
                <span style={touchStyles.summaryLabel}>Customer:</span>
                <span style={touchStyles.summaryValue}>
                  {order.guest_name || "Guest"}
                </span>
              </div>
              <div style={touchStyles.summaryRow}>
                <span style={touchStyles.summaryLabel}>Subtotal:</span>
                <span style={touchStyles.summaryValue}>
                  GHS {menuSubtotal.toFixed(2)}
                </span>
              </div>
              {isDiscountApplied && (
                <div style={{ ...touchStyles.summaryRow, color: "#DC2626" }}>
                  <span style={touchStyles.summaryLabel}>
                    Discount ({discountPercent}%):
                  </span>
                  <span style={touchStyles.summaryValue}>
                    -GHS {discountAmount.toFixed(2)}
                  </span>
                </div>
              )}
              {isDiscountApplied && (
                <div style={touchStyles.summaryRow}>
                  <span style={touchStyles.summaryLabel}>
                    Discounted Subtotal:
                  </span>
                  <span style={touchStyles.summaryValue}>
                    GHS {(menuSubtotal - discountAmount).toFixed(2)}
                  </span>
                </div>
              )}
              {hasServiceCharge && (
                <div style={touchStyles.summaryRow}>
                  <span style={touchStyles.summaryLabel}>
                    Service Charge ({serviceChargePercentage}%):
                  </span>
                  <span style={touchStyles.summaryValue}>
                    +GHS {serviceChargeAmount.toFixed(2)}
                  </span>
                </div>
              )}

              {(vatAmount > 0 ||
                nhilAmount > 0 ||
                getfundAmount > 0 ||
                tourismAmount > 0) && (
                <>
                  <div
                    style={{
                      borderTop: "1px solid #E2E8F0",
                      marginTop: "10px",
                      paddingTop: "10px",
                    }}
                  >
                    <div style={touchStyles.summaryRow}>
                      <span style={touchStyles.summaryLabel}>
                        Tax Breakdown:
                      </span>
                      <span style={touchStyles.summaryValue}></span>
                    </div>
                    {vatAmount > 0 && (
                      <div style={touchStyles.summaryRow}>
                        <span style={touchStyles.summaryLabel}>VAT 15%:</span>
                        <span style={touchStyles.summaryValue}>
                          GHS {vatAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {nhilAmount > 0 && (
                      <div style={touchStyles.summaryRow}>
                        <span style={touchStyles.summaryLabel}>NHIL 2.5%:</span>
                        <span style={touchStyles.summaryValue}>
                          GHS {nhilAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {getfundAmount > 0 && (
                      <div style={touchStyles.summaryRow}>
                        <span style={touchStyles.summaryLabel}>
                          GETFund 2.5%:
                        </span>
                        <span style={touchStyles.summaryValue}>
                          GHS {getfundAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {tourismAmount > 0 && (
                      <div style={touchStyles.summaryRow}>
                        <span style={touchStyles.summaryLabel}>
                          Tourism 1%:
                        </span>
                        <span style={touchStyles.summaryValue}>
                          GHS {tourismAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}

              <div
                style={{
                  borderTop: "2px solid #E2E8F0",
                  marginTop: "14px",
                  paddingTop: "14px",
                }}
              >
                <div style={touchStyles.summaryRow}>
                  <span
                    style={{
                      ...touchStyles.summaryLabel,
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#1E293B",
                    }}
                  >
                    Grand Total:
                  </span>
                  <span
                    style={{
                      ...touchStyles.summaryValue,
                      fontSize: "22px",
                      fontWeight: "800",
                      color: "#FF6B35",
                    }}
                  >
                    GHS {grandTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message={successMessage || "Payment successful!"}
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faClose}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </>
  );
}

const touchStyles = {
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modalContent: {
    position: "relative",
    backgroundColor: "#fff",
    borderRadius: "20px",
    width: "63%",
    maxWidth: "1000px",
    height: "80vh",
    maxHeight: "700px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 20px",
    borderBottom: "2px solid #E2E8F0",
    flexShrink: 0,
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: "700",
    margin: 0,
    color: "#1E293B",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    padding: "8px",
    borderRadius: "12px",
    color: "#64748B",
    minWidth: "40px",
    minHeight: "40px",
  },
  twoColumnLayout: {
    display: "flex",
    gap: "16px",
    padding: "16px",
    flex: 1,
    overflow: "hidden",
  },
  leftColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    overflowY: "auto",
    paddingRight: "8px",
  },
  rightColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    overflowY: "auto",
    paddingLeft: "8px",
  },
  discountSection: {
    backgroundColor: "#F8FAFC",
    padding: "12px",
    borderRadius: "12px",
    border: "1px solid #E2E8F0",
    flexShrink: 0,
  },
  discountHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "10px",
    paddingBottom: "8px",
    borderBottom: "1px solid #E2E8F0",
  },
  discountTitle: {
    fontWeight: "700",
    fontSize: "14px",
    color: "#1E293B",
  },
  discountStatus: {
    fontSize: "11px",
    color: "#10B981",
    fontWeight: "600",
    backgroundColor: "#D1FAE5",
    padding: "2px 8px",
    borderRadius: "12px",
  },
  discountButtons: {
    display: "flex",
    gap: "50px",
    flexWrap: "wrap",
  },
  discountButton: {
    padding: "8px 16px",
    border: "2px solid transparent",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
    minHeight: "56px",
    height: "56px",
    minWidth: "54px",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  serviceChargeSection: {
    flexShrink: 0,
  },
  serviceChargeRow: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  serviceChargeButtons: {
    flex: 1,
  },
  addServiceChargeButton: {
    width: "100%",
    padding: "10px 16px",
    backgroundColor: "#4CAF50",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "700",
    color: "white",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "56px",
    height: "56px",
    transition: "all 0.2s ease",
  },
  removeServiceChargeButton: {
    width: "100%",
    padding: "10px 16px",
    backgroundColor: "#EF4444",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "700",
    color: "white",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "56px",
    height: "56px",
    transition: "all 0.2s ease",
  },
  detailsButton: {
    padding: "10px 20px",
    backgroundColor: "#3B82F6",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "700",
    color: "white",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "56px",
    height: "56px",
    minWidth: "100px",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "8px",
    fontSize: "14px",
  },
  summaryLabel: {
    color: "#64748B",
  },
  summaryValue: {
    fontWeight: "600",
    color: "#1E293B",
  },
  totalSection: {
    backgroundColor: "#F2F2F2",
    padding: "16px",
    borderRadius: "12px",
    flexShrink: 0,
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  totalLabel: {
    fontSize: "16px",
    color: "#64748B",
    fontWeight: "600",
  },
  totalAmount: {
    fontSize: "26px",
    fontWeight: "800",
    color: "#FF6B35",
  },
  actionButtons: {
    display: "flex",
    gap: "10px",
    marginTop: "4px",
    flexShrink: 0,
  },
  printBillButton: {
    flex: 1,
    padding: "14px",
    backgroundColor: "#3B82F6",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "700",
    color: "white",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "56px",
    height: "56px",
  },
  completePayButton: {
    flex: 1,
    padding: "14px",
    backgroundColor: "#4CAF50",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "700",
    color: "white",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "auto",
    flexShrink: 0,
    minHeight: "56px",
    height: "56px",
  },
  paymentMethodsSection: {
    flexShrink: 0,
  },
  label: {
    display: "block",
    marginBottom: "10px",
    fontWeight: "600",
    fontSize: "15px",
    color: "#1E293B",
  },
  paymentMethodsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "16px",
  },
  paymentMethodButton: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px",
    border: "2px solid #E2E8F0",
    borderRadius: "12px",
    backgroundColor: "white",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
    minHeight: "56px",
    height: "56px",
  },
  paymentMethodLabel: {
    fontSize: "15px",
    fontWeight: "600",
  },
  cashSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "22px",
    minHeight: 0,
  },
  paymentSummaryRow: {
    display: "flex",
    gap: "12px",
    marginBottom: "12px",
  },
  amountCard: {
    flex: 1,
    backgroundColor: "#F8FAFC",
    border: "2px solid #E2E8F0",
    borderRadius: "10px",
    padding: "14px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "76px",
  },
  amountCardLabel: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#64748B",
    marginBottom: "6px",
  },
  amountCardValue: {
    fontSize: "24px",
    fontWeight: "800",
    color: "#1E293B",
  },
  nonCashMessage: {
    padding: "14px",
    backgroundColor: "#E0F2FE",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: "600",
    color: "#0284C7",
    textAlign: "center",
    border: "1px solid #BAE6FD",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "48px",
  },
};
